import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Loader2, Eye, EyeOff, Shield, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { getErrorMessage } from "@/lib/errors";
import api from "@/lib/api";

export default function Pro2FASetupModal({ open, onClose, onComplete }) {
  const [step, setStep] = useState("password"); // "password" | "scan" | "verify" | "done"
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [qrCode, setQrCode] = useState("");
  const [secret, setSecret] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (open) {
      setStep("password");
      setPassword("");
      setShowPassword(false);
      setQrCode("");
      setSecret("");
      setCode("");
      setLoading(false);
      setCopied(false);
    }
  }, [open]);

  const handleSetup = async (e) => {
    e.preventDefault();
    if (!password) return;
    setLoading(true);
    try {
      const { data } = await api.post("/pro/2fa/setup", { password });
      setQrCode(data.qr_code);
      setSecret(data.secret);
      setStep("scan");
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to start 2FA setup"));
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    if (code.length !== 6) return;
    setLoading(true);
    try {
      await api.post("/pro/2fa/enable", { code });
      setStep("done");
    } catch (err) {
      toast.error(getErrorMessage(err, "Invalid code"));
    } finally {
      setLoading(false);
    }
  };

  const handleCopySecret = () => {
    navigator.clipboard.writeText(secret);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDone = () => {
    onComplete?.();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && step !== "scan" && step !== "verify") onClose?.(); }}>
      <DialogContent className="sm:max-w-[420px] p-0 [&>button]:hidden" data-testid="pro-2fa-setup-modal">
        <div className="max-h-[85vh] overflow-y-auto p-6">

        {step === "password" && (
          <>
            <DialogHeader>
              <div className="w-10 h-10 rounded-xl bg-zinc-900 flex items-center justify-center mb-2">
                <Shield size={20} className="text-white" />
              </div>
              <DialogTitle className="text-lg">Enable two-factor authentication</DialogTitle>
              <DialogDescription className="text-sm text-zinc-500">
                Protect your account with an authenticator app. Enter your password to continue.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSetup} className="space-y-4 pt-4">
              <div className="space-y-1.5">
                <Label className="text-xs text-zinc-500">Current password</Label>
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    className="pr-10"
                    autoFocus
                    data-testid="2fa-setup-password"
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-[#888888]">
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={onClose} className="flex-1 h-10">Cancel</Button>
                <Button type="submit" disabled={loading || !password} className="flex-1 bg-zinc-900 hover:bg-zinc-800 text-white h-10" data-testid="2fa-setup-continue">
                  {loading ? <Loader2 size={16} className="animate-spin" /> : "Continue"}
                </Button>
              </div>
            </form>
          </>
        )}

        {step === "scan" && (
          <>
            <DialogHeader>
              <DialogTitle className="text-lg">Scan QR code</DialogTitle>
              <DialogDescription className="text-sm text-zinc-500">
                Scan this code with your authenticator app (Google Authenticator, Authy, etc.)
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col items-center py-4">
              {qrCode && <img src={qrCode} alt="2FA QR Code" className="w-48 h-48 rounded-lg border border-[#E5E5E5]" data-testid="2fa-qr-code" />}
            </div>
            <div className="bg-[#F5F5F5] border border-[#E5E5E5] rounded-lg p-3 mb-4">
              <p className="text-[10px] text-zinc-500 mb-1">Or enter this key manually:</p>
              <div className="flex items-center gap-2">
                <code className="text-xs font-mono text-[#555555] flex-1 break-all" data-testid="2fa-manual-key">{secret}</code>
                <button onClick={handleCopySecret} className="text-zinc-500 hover:text-[#888888] shrink-0">
                  {copied ? <Check size={14} className="text-emerald-500" /> : <Copy size={14} />}
                </button>
              </div>
            </div>
            <Button onClick={() => setStep("verify")} className="w-full bg-zinc-900 hover:bg-zinc-800 text-white h-10" data-testid="2fa-setup-next">
              I've scanned the code
            </Button>
          </>
        )}

        {step === "verify" && (
          <>
            <DialogHeader>
              <DialogTitle className="text-lg">Verify setup</DialogTitle>
              <DialogDescription className="text-sm text-zinc-500">
                Enter the 6-digit code from your authenticator app to confirm setup.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleVerify} className="space-y-4 pt-4">
              <div className="space-y-1.5">
                <Label className="text-xs text-zinc-500">6-digit code</Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="000000"
                  className="text-center text-lg font-mono tracking-[0.3em]"
                  autoFocus
                  data-testid="2fa-setup-code"
                />
              </div>
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={() => setStep("scan")} className="flex-1 h-10">Back</Button>
                <Button type="submit" disabled={loading || code.length !== 6} className="flex-1 bg-zinc-900 hover:bg-zinc-800 text-white h-10" data-testid="2fa-setup-verify">
                  {loading ? <Loader2 size={16} className="animate-spin" /> : "Verify & Enable"}
                </Button>
              </div>
            </form>
          </>
        )}

        {step === "done" && (
          <>
            <DialogHeader>
              <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center mb-2">
                <Shield size={20} className="text-emerald-600" />
              </div>
              <DialogTitle className="text-lg">2FA is now active</DialogTitle>
              <DialogDescription className="text-sm text-zinc-500">
                Your account is now protected with two-factor authentication. You'll need your authenticator app every time you log in.
              </DialogDescription>
            </DialogHeader>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-3">
              <p className="text-xs text-amber-800">
                If you lose access to your authenticator app, use one of your recovery codes to log in.
              </p>
            </div>
            <Button onClick={handleDone} className="w-full bg-zinc-900 hover:bg-zinc-800 text-white h-10 mt-4" data-testid="2fa-setup-done">
              Done
            </Button>
          </>
        )}

        </div>
      </DialogContent>
    </Dialog>
  );
}
